// Catches a render error so one broken page does not blank the whole portal. The fallback shows
// plain text and a retry; the error itself is never rendered (no stack, no internals).
import { Component, type ReactNode } from 'react';
import { userMessage } from './error-message.ts';

export function ErrorPanel(props: {
  readonly error: unknown;
  readonly onRetry: () => void;
  readonly title?: string;
}): React.JSX.Element {
  return (
    <section className="error-panel" role="alert">
      <h2 className="error-panel__title">{props.title ?? 'This page could not be shown'}</h2>
      <p className="error-panel__body">{userMessage(props.error)}</p>
      <button type="button" className="btn btn--primary" onClick={props.onRetry}>
        Try again
      </button>
    </section>
  );
}

interface BoundaryProps {
  readonly children: ReactNode;
  /** Changing this clears a caught error (e.g. the current path, so navigating away recovers). */
  readonly resetKey?: string;
}

interface BoundaryState {
  // A flag, not `error !== null`: code may throw null or undefined.
  readonly failed: boolean;
  readonly error: unknown;
  readonly resetKey: string | undefined;
}

export class ErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { failed: false, error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: unknown): Partial<BoundaryState> {
    return { failed: true, error };
  }

  static getDerivedStateFromProps(
    props: BoundaryProps,
    state: BoundaryState,
  ): Partial<BoundaryState> | null {
    if (props.resetKey === state.resetKey) return null;
    return { failed: false, error: null, resetKey: props.resetKey };
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <ErrorPanel
        error={this.state.error}
        onRetry={() => this.setState({ failed: false, error: null })}
      />
    );
  }
}
